import { useState, useRef, useEffect, useCallback } from 'react';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';

interface ImageCropperProps {
  image: string;
  onCrop: (croppedImageUrl: string) => void;
  onCancel: () => void;
  size?: number; // Size of the circular crop area in pixels
}

export default function ImageCropper({ image, onCrop, onCancel, size = 200 }: ImageCropperProps) {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [previewImage, setPreviewImage] = useState<string>('');
  const [processedImage, setProcessedImage] = useState<string>(''); // Scaled-down image for display
  const [originalImageUrl, setOriginalImageUrl] = useState<string>(''); // Original high-res image URL
  const [initialScale, setInitialScale] = useState(1);
  const [displayDimensions, setDisplayDimensions] = useState<{ width: number; height: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const hasCenteredRef = useRef(false);

  useEffect(() => {
    // Reset position and scale when image changes
    setPosition({ x: 0, y: 0 });
    setProcessedImage('');
    setOriginalImageUrl(image); // Store original image URL
    setDisplayDimensions(null);
    hasCenteredRef.current = false; // Reset centering flag for new image
    
    // Pre-process image: scale down if too large for easier positioning
    const img = new Image();
    img.onload = () => {
      const MAX_DISPLAY_SIZE = 800; // Max dimension for display in cropper
      const originalWidth = img.width;
      const originalHeight = img.height;
      const maxDimension = Math.max(originalWidth, originalHeight);
      
      let displayWidth = originalWidth;
      let displayHeight = originalHeight;
      
      if (maxDimension > MAX_DISPLAY_SIZE) {
        // Scale down for display
        const scaleFactor = MAX_DISPLAY_SIZE / maxDimension;
        displayWidth = Math.round(originalWidth * scaleFactor);
        displayHeight = Math.round(originalHeight * scaleFactor);
        
        const canvas = document.createElement('canvas');
        canvas.width = displayWidth;
        canvas.height = displayHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
          const scaledDataUrl = canvas.toDataURL('image/jpeg', 0.9);
          setProcessedImage(scaledDataUrl);
          setDisplayDimensions({ width: displayWidth, height: displayHeight });
        } else {
          // Fallback: use original image if canvas fails
          setProcessedImage(image);
          setDisplayDimensions({ width: displayWidth, height: displayHeight });
        }
      } else {
        // Image is already small enough, use as-is
        setProcessedImage(image);
        setDisplayDimensions({ width: displayWidth, height: displayHeight });
      }
      
      // Calculate smart initial scale based on image size vs crop area
      // For a circular crop, we need to ensure the image fills the circle
      // The minimum dimension (width or height) when scaled must be at least the circle diameter
      const cropSize = size;
      const imageMinDimension = Math.min(displayWidth, displayHeight);
      const imageMaxDimension = Math.max(displayWidth, displayHeight);
      
      // Calculate minimum scale needed to fill the circle
      // Both width and height when scaled must be >= cropSize
      const minScaleToFill = Math.max(
        cropSize / displayWidth,  // Scale needed for width to fill circle
        cropSize / displayHeight  // Scale needed for height to fill circle
      );
      
      // Add a small buffer (1.1x) so there's room to adjust, but ensure it fills the circle
      const fitScale = minScaleToFill * 1.1;
      
      // For very large images, allow more zoom out, but always ensure circle is filled
      // For smaller images, can zoom in more
      const calculatedScale = imageMinDimension > cropSize * 2
        ? Math.max(minScaleToFill, Math.min(1.5, fitScale)) // Large images: ensure fill, allow zoom out
        : Math.max(minScaleToFill, Math.min(2.0, fitScale)); // Smaller images: ensure fill, allow more zoom in
      
      setInitialScale(calculatedScale);
      setScale(calculatedScale);
    };
    img.onerror = () => {
      console.error('Failed to load image');
      // Still try to set the image and dimensions as fallback
      setProcessedImage(image);
      setDisplayDimensions({ width: 200, height: 200 }); // Default fallback dimensions
    };
    img.src = image;
  }, [image, size]);

  // Center the image in the crop circle when it first loads
  useEffect(() => {
    if (!processedImage || !imageRef.current || !containerRef.current || !displayDimensions) return;
    
    // Wait for image to load and DOM to be ready
    const img = imageRef.current;
    const centerImage = () => {
      if (!imageRef.current || !containerRef.current || !displayDimensions) return;
      
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        if (!imageRef.current || !containerRef.current || !displayDimensions) return;
        
        const container = containerRef.current;
        
        // Get container dimensions (crop circle area)
        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;
        
        // Use the current scale
        const currentScale = scale;
        
        // Calculate the displayed image's size (after transform scale)
        // Use actual image dimensions, not container dimensions
        const displayedImgWidth = displayDimensions.width * currentScale;
        const displayedImgHeight = displayDimensions.height * currentScale;
        
        // Center of the container (crop circle center)
        const containerCenterX = containerWidth / 2;
        const containerCenterY = containerHeight / 2;
        
        // Center of the displayed image (with top-left origin, center is at top-left + half size)
        const imageCenterX = displayedImgWidth / 2;
        const imageCenterY = displayedImgHeight / 2;
        
        // Calculate position to center the image
        // Position is the offset of the image's top-left corner
        // With top-left transform origin, we position so the center aligns
        const centeredX = containerCenterX - imageCenterX;
        const centeredY = containerCenterY - imageCenterY;
        
        setPosition({ x: centeredX, y: centeredY });
        hasCenteredRef.current = true;
      });
    };
    
    // Reset centering flag when image or dimensions change
    if (!hasCenteredRef.current) {
      if (img.complete && img.naturalWidth > 0) {
        // Image already loaded, center immediately
        centerImage();
      } else {
        // Wait for image to load
        img.onload = centerImage;
      }
    }
  }, [processedImage, scale, displayDimensions]); // Depend on processedImage, scale, and displayDimensions

  const updatePreview = useCallback(async () => {
    if (!imageRef.current || !containerRef.current || !previewCanvasRef.current || !originalImageUrl || !displayDimensions) return;

    try {
      // Load the original high-res image for preview (same as final crop)
      const originalImg = new Image();
      await new Promise((resolve, reject) => {
        originalImg.onload = resolve;
        originalImg.onerror = () => {
          console.error('Failed to load original image for preview');
          reject(new Error('Failed to load original image'));
        };
        originalImg.src = originalImageUrl;
      });

      const canvas = previewCanvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Failed to get canvas context');
        return;
      }

      // Set canvas size for preview (smaller size for calendar preview)
      const previewSize = 40; // Size of avatar in calendar
      canvas.width = previewSize;
      canvas.height = previewSize;

      // Create circular clipping path
      ctx.beginPath();
      ctx.arc(previewSize / 2, previewSize / 2, previewSize / 2, 0, 2 * Math.PI);
      ctx.clip();

      // Use the displayed (scaled) image for position calculations
      const displayedImg = imageRef.current;
      const container = containerRef.current;

      // Get container and displayed image dimensions
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const displayedWidth = displayDimensions.width;
      const displayedHeight = displayDimensions.height;
      const originalWidth = originalImg.naturalWidth;
      const originalHeight = originalImg.naturalHeight;
      
      // Calculate scale factor from displayed image to original image
      const scaleToOriginalX = originalWidth / displayedWidth;
      const scaleToOriginalY = originalHeight / displayedHeight;
      
      // The crop circle is centered in the container
      const cropCenterX = containerWidth / 2;
      const cropCenterY = containerHeight / 2;
      
      // Calculate the displayed image's size (after transform scale)
      // Use actual image dimensions multiplied by scale
      const displayedImgWidth = displayedWidth * scale;
      const displayedImgHeight = displayedHeight * scale;
      
      // Calculate the crop center position in displayed image coordinates
      const displayedCropCenterX = cropCenterX - position.x;
      const displayedCropCenterY = cropCenterY - position.y;
      
      // Convert displayed coordinates to original image coordinates
      const scaleX = displayedWidth / displayedImgWidth;
      const scaleY = displayedHeight / displayedImgHeight;
      
      const sourceCenterX = displayedCropCenterX * scaleX * scaleToOriginalX;
      const sourceCenterY = displayedCropCenterY * scaleY * scaleToOriginalY;
      const sourceRadius = (size / 2) * scaleX * scaleToOriginalX;
      
      // Calculate source rectangle (square that fits the circle)
      const sourceSize = sourceRadius * 2;
      const sourceX = sourceCenterX - sourceRadius;
      const sourceY = sourceCenterY - sourceRadius;
      
      // Clamp to image bounds
      const clampedSourceX = Math.max(0, Math.min(sourceX, originalWidth - sourceSize));
      const clampedSourceY = Math.max(0, Math.min(sourceY, originalHeight - sourceSize));
      const clampedSourceSize = Math.min(sourceSize, originalWidth - clampedSourceX, originalHeight - clampedSourceY);

      // Draw the cropped image from the original high-res image
      ctx.drawImage(
        originalImg,
        clampedSourceX,
        clampedSourceY,
        clampedSourceSize,
        clampedSourceSize,
        0,
        0,
        previewSize,
        previewSize
      );

      // Convert to JPEG for preview (smaller file size)
      const previewDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setPreviewImage(previewDataUrl);
    } catch (error) {
      console.error('Error generating preview:', error);
      // Don't set preview image on error, will show placeholder
    }
  }, [position, scale, originalImageUrl, size, displayDimensions]);

  // Update preview whenever position or scale changes
  useEffect(() => {
    if (processedImage && originalImageUrl && displayDimensions) {
      updatePreview();
    }
  }, [position, scale, processedImage, originalImageUrl, displayDimensions, updatePreview]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    setDragStart({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const touch = e.touches[0];
    setPosition({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y,
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    // Allow zooming out more for large images
    const minScale = initialScale < 0.5 ? 0.2 : 0.3; // Lower minimum for pre-zoomed images
    setScale((prev) => Math.max(prev - 0.1, minScale));
  };

  const handleScaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newScale = parseFloat(e.target.value);
    setScale(newScale);
  };

  // Calculate min and max scale for slider
  // Use initialScale if available, otherwise default to 0.3
  const minScale = initialScale && initialScale < 0.5 ? 0.2 : 0.3;
  const maxScale = 3;

  const handleCrop = async () => {
    if (!imageRef.current || !containerRef.current || !originalImageUrl) return;

    // Maximum output size for the cropped image (to keep file size manageable)
    const MAX_OUTPUT_SIZE = 400; // 400x400px is good for avatars and keeps file size small
    
    // Load the original high-res image
    const originalImg = new Image();
    await new Promise((resolve, reject) => {
      originalImg.onload = resolve;
      originalImg.onerror = reject;
      originalImg.src = originalImageUrl;
    });
    
    const canvas = document.createElement('canvas');
    canvas.width = MAX_OUTPUT_SIZE;
    canvas.height = MAX_OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Note: We store as square, display as circle using CSS border-radius
    // No circular clipping needed here - just draw the square image

    // Use the displayed (scaled) image for position calculations
    const displayedImg = imageRef.current;
    const container = containerRef.current;

    // Get container dimensions
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;
    
    // Use displayDimensions (the processed/scaled-down image dimensions) for calculations
    // This is the actual size of the image being displayed, not the original
    if (!displayDimensions) {
      console.error('Display dimensions not available');
      return;
    }
    
    const displayedWidth = displayDimensions.width;
    const displayedHeight = displayDimensions.height;
    const originalWidth = originalImg.naturalWidth;
    const originalHeight = originalImg.naturalHeight;
    
    // Calculate scale factor from displayed image to original image
    const scaleToOriginalX = originalWidth / displayedWidth;
    const scaleToOriginalY = originalHeight / displayedHeight;
    
    // The crop circle is centered in the container
    const cropCenterX = containerWidth / 2;
    const cropCenterY = containerHeight / 2;
    
    // Calculate the displayed image's size (after transform scale)
    // This is the actual size of the image element after CSS transform scale
    const displayedImgWidth = displayedWidth * scale;
    const displayedImgHeight = displayedHeight * scale;
    
    // Calculate the crop center position in displayed image coordinates
    // The position is the offset of the image's top-left corner
    // So the crop center relative to the image is: container center - position
    const displayedCropCenterX = cropCenterX - position.x;
    const displayedCropCenterY = cropCenterY - position.y;
    
    // Convert displayed coordinates to original image coordinates
    // First, convert from scaled displayed size to actual displayed size
    const scaleX = displayedWidth / displayedImgWidth;
    const scaleY = displayedHeight / displayedImgHeight;
    
    const sourceCenterX = displayedCropCenterX * scaleX * scaleToOriginalX;
    const sourceCenterY = displayedCropCenterY * scaleY * scaleToOriginalY;
    const sourceRadius = (size / 2) * scaleX * scaleToOriginalX;
    
    // Calculate source rectangle (square that fits the circle)
    const sourceSize = sourceRadius * 2;
    const sourceX = sourceCenterX - sourceRadius;
    const sourceY = sourceCenterY - sourceRadius;
    
    // Clamp to image bounds
    const clampedSourceX = Math.max(0, Math.min(sourceX, originalWidth - sourceSize));
    const clampedSourceY = Math.max(0, Math.min(sourceY, originalHeight - sourceSize));
    const clampedSourceSize = Math.min(sourceSize, originalWidth - clampedSourceX, originalHeight - clampedSourceY);

    // Draw the cropped image from the original high-res image, scaled to MAX_OUTPUT_SIZE
    ctx.drawImage(
      originalImg,
      clampedSourceX,
      clampedSourceY,
      clampedSourceSize,
      clampedSourceSize,
      0,
      0,
      MAX_OUTPUT_SIZE,
      MAX_OUTPUT_SIZE
    );

    // Convert to JPEG with compression (smaller file size than PNG)
    // Quality of 0.85 provides good balance between quality and file size
    const croppedImageUrl = canvas.toDataURL('image/jpeg', 0.85);
    onCrop(croppedImageUrl);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Position Your Photo</h2>
        <p className="text-gray-600 mb-4">Drag to move, use slider to zoom</p>

        {/* Crop Area - Show full image with circular overlay */}
        <div className="relative mx-auto bg-gray-100 rounded-sm p-6 overflow-hidden" style={{ width: size + 120, height: size + 120 }}>
          <div
            ref={containerRef}
            className="relative mx-auto"
            style={{ 
              width: size, 
              height: size,
              overflow: 'hidden', // Constrain image to this area
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Full image - visible outside the crop circle but constrained to container */}
            {processedImage && (
              <img
                ref={imageRef}
                src={processedImage}
                alt="Crop preview"
                className="absolute"
                style={{
                  transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                  transformOrigin: 'top left',
                  width: displayDimensions?.width || 'auto',
                  height: displayDimensions?.height || 'auto',
                  minWidth: size,
                  minHeight: size,
                  maxWidth: 'none',
                  maxHeight: 'none',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                  pointerEvents: 'none',
                }}
                draggable={false}
                onLoad={(e) => {
                  // If dimensions weren't set, get them from the loaded image
                  if (!displayDimensions && imageRef.current) {
                    const img = imageRef.current;
                    const imgWidth = img.naturalWidth || img.width || size;
                    const imgHeight = img.naturalHeight || img.height || size;
                    setDisplayDimensions({ 
                      width: imgWidth, 
                      height: imgHeight 
                    });
                    
                    // Center the image after setting dimensions
                    if (containerRef.current && !hasCenteredRef.current) {
                      const container = containerRef.current;
                      const containerWidth = container.offsetWidth;
                      const containerHeight = container.offsetHeight;
                      const currentScale = scale;
                      const displayedImgWidth = imgWidth * currentScale;
                      const displayedImgHeight = imgHeight * currentScale;
                      const containerCenterX = containerWidth / 2;
                      const containerCenterY = containerHeight / 2;
                      const imageCenterX = displayedImgWidth / 2;
                      const imageCenterY = displayedImgHeight / 2;
                      setPosition({ 
                        x: containerCenterX - imageCenterX, 
                        y: containerCenterY - imageCenterY 
                      });
                      hasCenteredRef.current = true;
                    }
                  } else if (containerRef.current && displayDimensions && !hasCenteredRef.current) {
                    // Center if dimensions are already set but centering hasn't happened
                    const container = containerRef.current;
                    const containerWidth = container.offsetWidth;
                    const containerHeight = container.offsetHeight;
                    const currentScale = scale;
                    const displayedImgWidth = displayDimensions.width * currentScale;
                    const displayedImgHeight = displayDimensions.height * currentScale;
                    const containerCenterX = containerWidth / 2;
                    const containerCenterY = containerHeight / 2;
                    const imageCenterX = displayedImgWidth / 2;
                    const imageCenterY = displayedImgHeight / 2;
                    setPosition({ 
                      x: containerCenterX - imageCenterX, 
                      y: containerCenterY - imageCenterY 
                    });
                    hasCenteredRef.current = true;
                  }
                  updatePreview();
                }}
              />
            )}
            {/* Circular crop overlay - shows what will be cropped */}
            <div
              className="absolute inset-0 pointer-events-none z-10"
              style={{
                width: size,
                height: size,
                borderRadius: '50%',
                border: '4px solid rgba(20, 184, 166, 0.8)',
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>
        </div>

        {/* Controls */}
        <div className="mt-8">
          <div className="flex items-center justify-center gap-4 mb-3">
            <span className="text-gray-600 font-medium text-sm min-w-[50px] text-right">
              {Math.round(minScale * 100)}%
            </span>
            <div className="flex-1 max-w-md relative">
              <input
                type="range"
                min={minScale}
                max={maxScale}
                step={0.01}
                value={scale}
                onChange={handleScaleChange}
                className="w-full h-4 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((scale - minScale) / (maxScale - minScale)) * 100}%, #e5e7eb ${((scale - minScale) / (maxScale - minScale)) * 100}%, #e5e7eb 100%)`,
                  WebkitAppearance: 'none',
                  MozAppearance: 'none',
                }}
                aria-label="Zoom level"
              />
              <style>{`
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  background: #3b82f6;
                  cursor: pointer;
                  border: 2px solid white;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }
                input[type="range"]::-moz-range-thumb {
                  width: 24px;
                  height: 24px;
                  border-radius: 50%;
                  background: #3b82f6;
                  cursor: pointer;
                  border: 2px solid white;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }
              `}</style>
            </div>
            <span className="text-gray-600 font-medium text-sm min-w-[50px] text-left">
              {Math.round(maxScale * 100)}%
            </span>
          </div>
          <div className="text-center">
            <span className="text-gray-800 font-semibold text-xl">
              {Math.round(scale * 100)}%
            </span>
          </div>
        </div>

        {/* Calendar Preview */}
        <div className={`mt-6 ${tokens.button.secondary} border-2 ${tokens.border.light} rounded-landing p-4`}>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Preview in Calendar</h3>
          <div className="bg-white rounded-sm p-4 shadow-sm border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="font-bold text-base text-gray-800 mb-1">Sample Event</div>
                <div className="text-sm text-gray-600 mb-2">3:00 PM - 4:00 PM</div>
                <div className="text-xs text-gray-500">📍 Event Location</div>
              </div>
              <div className="ml-3 flex-shrink-0">
                {previewImage ? (
                  <img
                    src={previewImage}
                    alt="Calendar preview"
                    className="w-10 h-10 rounded-sm-full object-cover border-2 border-white shadow-sm"
                  />
                ) : (
                  <div className={`w-10 h-10 rounded-full ${tokens.button.primary} border-2 ${tokens.card.bg} shadow-sm flex items-center justify-center ${tokens.text.inverse} font-bold text-sm`}>
                    ?
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            This is how your photo will appear in calendar events
          </p>
        </div>

        {/* Hidden canvas for preview generation */}
        <canvas ref={previewCanvasRef} className="hidden" />

        {/* Action Buttons */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-sm transition-colors touch-target min-h-[60px]"
          >
            Cancel
          </button>
          <button
            onClick={handleCrop}
            className={`flex-1 px-6 py-3 ${tokens.button.primary} ${tokens.text.inverse} font-semibold rounded-landing transition-colors touch-target min-h-[60px]`}
          >
            Use This Photo
          </button>
        </div>
      </div>
    </div>
  );
}

